// First-party pageview tracking. Subscribes to react-router location changes
// and fires a fire-and-forget `page.viewed` telemetry event per navigation.
//
// Mount ONCE high in the tree (RouteTracker, just inside <BrowserRouter>).
// Never blocks navigation; errors are swallowed in trackAnon. Identical
// consecutive paths are deduped so a `replace` to the same path or a render
// loop doesn't double-count.

import { captureFirstTouchUtm, trackPageview } from '@/lib/telemetry';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

export function usePageviewTracking(): void {
  const location = useLocation();
  const lastPath = useRef<string | null>(null);
  const isFirst = useRef(true);

  useEffect(() => {
    const path = location.pathname + location.search;

    // First-touch UTM is captured from the initial URL before it's used. Run
    // on first render so the very first pageview carries attribution.
    if (isFirst.current) captureFirstTouchUtm();

    if (lastPath.current === path) return;
    lastPath.current = path;

    trackPageview(location.pathname, { firstLoad: isFirst.current });
    isFirst.current = false;
  }, [location.pathname, location.search]);
}
